import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Volume2, TrendingUp, Sparkles, ArrowRight, Brain, FileText, Zap, Globe, Layers, Award, Users, 
  Activity, Star, ChevronRight, Check, HeartHandshake, Eye, Compass, GraduationCap, Stethoscope, 
  ClipboardList, CreditCard, Box, Bot, ChevronDown, ChevronUp, ShieldCheck, Play, RotateCcw, 
  Smartphone, ArrowDownToLine, Cpu, Grid, Sun, Video, Heart, BarChart2, CheckCircle2, 
  ArrowUpRight, Music, Clock, Sliders, MessageSquare, Mic, Headphones
} from 'lucide-react';

import { 
  AbilityBrandLogo, 
  SunIcon, 
  ClarvosPillBadge, 
  ClarvosCTAButton 
} from './ClarvosBrand';
import { ClinicalComparisonChart } from './ClinicalComparisonChart';
import { HeroClinicalShowcase } from './HeroClinicalShowcase';
import { DISABILITY_CLUSTERS } from '../data/disabilityTaxonomy';

interface SaaSLandingPageProps {
  onOpenAuth: () => void;
  onExploreDemo: (tab: any) => void;
  onSelectModule?: (moduleId: string) => void;
  onRequestDemo?: () => void;
}

export const SaaSLandingPage: React.FC<SaaSLandingPageProps> = ({ 
  onOpenAuth, 
  onExploreDemo, 
  onSelectModule,
  onRequestDemo 
}) => {
  const { t, i18n } = useTranslation();
  const [pricingCycle, setPricingCycle] = useState<'monthly' | 'annual'>('annual');

  // Interactive Assistive Demos State
  // 1. Speech synthesis
  const [speechPhrase, setSpeechPhrase] = useState(
    i18n.language === 'hi' ? 'मुझे पानी चाहिए' : i18n.language === 'mr' ? 'मला पाणी हवे आहे' : 'I want water'
  );
  const [speechPitch, setSpeechPitch] = useState(1.0);
  const [speechRate, setSpeechRate] = useState(1.0);

  // 2. CP Dwell Simulator
  const [dwellDelay, setDwellDelay] = useState(400);
  const [dwellProgress, setDwellProgress] = useState(0);
  const [dwellSuccess, setDwellSuccess] = useState(false);
  const dwellTimerRef = useRef<any>(null);

  // 3. Neuro-Rehab Metronome
  const [rasBpm, setRasBpm] = useState(80);
  const [isMetronomeActive, setIsMetronomeActive] = useState(false);

  // 4. Braille Input
  const [brailleInput, setBrailleInput] = useState('ABILITY');

  // 5. FAQ Accordion State
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  // Audio tone helper
  const playTone = (freq = 432, duration = 0.35, type: OscillatorType = 'sine') => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + duration);
    } catch (e) {
      console.warn('Audio playTone error:', e);
    }
  };

  // Braille mapper
  const toBraille = (text: string): string => {
    const brailleMap: Record<string, string> = {
      'a': '⠁', 'b': '⠃', 'c': '⠉', 'd': '⠙', 'e': '⠑', 'f': '⠋', 'g': '⠛', 'h': '⠓',
      'i': '⠊', 'j': '⠚', 'k': '⠅', 'l': '⠇', 'm': '⠍', 'n': '⠝', 'o': '⠕', 'p': '⠏',
      'q': '⠟', 'r': '⠗', 's': '⠎', 't': '⠞', 'u': '⠥', 'v': '⠧', 'w': '⠺', 'x': '⠭',
      'y': '⠽', 'z': '⠵', ' ': '⠀', '1': '⠂', '2': '⠆', '3': '⠒', '4': '⠲', '5': '⠢',
      '6': '⠖', '7': '⠶', '8': '⠦', '9': '⠔', '0': '⠴'
    };
    return text.toLowerCase().split('').map(c => brailleMap[c] || c).join('');
  };

  // Web Speech helper
  const handleSpeakSpeech = (text: string, pitch = 1.0, rate = 1.0) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.pitch = pitch;
      u.rate = rate;
      if (i18n.language === 'hi') u.lang = 'hi-IN';
      else if (i18n.language === 'mr') u.lang = 'mr-IN';
      else u.lang = 'en-US';
      window.speechSynthesis.speak(u);
    }
  };

  // Metronome effect
  useEffect(() => {
    let timer: any = null;
    if (isMetronomeActive) {
      const intervalMs = (60 / rasBpm) * 1000;
      playTone(880, 0.05, 'triangle');
      timer = setInterval(() => {
        playTone(880, 0.05, 'triangle');
      }, intervalMs);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isMetronomeActive, rasBpm]);

  // Dwell timer handlers
  const handleDwellStart = () => {
    setDwellProgress(0);
    setDwellSuccess(false);
    const start = Date.now();
    dwellTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const progress = Math.min(100, Math.round((elapsed / dwellDelay) * 100));
      setDwellProgress(progress);
      if (progress >= 100) {
        clearInterval(dwellTimerRef.current);
        setDwellSuccess(true);
        playTone(600, 0.25, 'sine');
      }
    }, 20);
  };

  const handleDwellCancel = () => {
    if (dwellTimerRef.current) clearInterval(dwellTimerRef.current);
    if (!dwellSuccess) {
      setDwellProgress(0);
    }
  };

  const handleDemoClick = () => {
    if (onRequestDemo) onRequestDemo();
    else onOpenAuth();
  };

  return (
    <div className="bg-clarvos-cream text-clarvos-black selection:bg-clarvos-yellow selection:text-clarvos-black">
      
      {/* ========================================================================= */}
      {/* 1. HERO SECTION - OBSIDIAN DARK HERO WITH 4-CARD CLINICAL SHOWCASE        */}
      {/* ========================================================================= */}
      <section className="bg-[#121114] text-white pt-28 pb-16 px-4 sm:px-8 lg:px-12 rounded-b-[44px] relative overflow-hidden text-center shadow-2xl">
        
        {/* Subtle ambient radial glows */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-gradient-to-b from-[#ffd64e]/10 to-transparent rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-10 w-80 h-80 bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-5xl mx-auto relative z-10">
          
          {/* Centered Main Brand Title */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white tracking-[-0.03em] leading-tight mb-4">
            AbilityOS
          </h1>

          {/* Centered Subtitle */}
          <p className="text-lg sm:text-2xl text-slate-300 font-medium max-w-2xl mx-auto mb-8 leading-snug">
            The Hardware-Free Clinical Suite for Speech and Audiology.
          </p>

          {/* Dual Centered Capsule Badges (Mockup Parity) */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-xs sm:text-sm font-mono font-bold uppercase tracking-wider text-white shadow-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span>100% HARDWARE-FREE CLINICAL SUITE</span>
            </div>

            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/60 backdrop-blur-md border border-white/15 text-xs sm:text-sm font-mono font-bold uppercase tracking-wider text-[#ffd64e] shadow-sm">
              <Sparkles className="w-3.5 h-3.5 fill-current" />
              <span>REPLACES $15,000+ PROPRIETARY EQUIPMENT</span>
            </div>
          </div>

          {/* Action CTAs */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-12">
            <ClarvosCTAButton
              text="Request a Demo"
              hoverText="Talk to Specialists"
              onClick={handleDemoClick}
              variant="yellow"
              size="lg"
            />
            <ClarvosCTAButton
              text="Launch 30-Module Sandbox"
              hoverText="Interactive Clinic"
              onClick={() => onExploreDemo('modules')}
              variant="dark"
              size="lg"
              className="border border-white/20"
            />
            <a
              href="#comparison"
              className="px-6 py-3.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold text-xs sm:text-sm border border-white/20 transition-all flex items-center gap-1.5 shadow-sm"
            >
              <span>Compare Competitors</span>
              <ChevronRight className="w-4 h-4 text-[#ffd64e]" />
            </a>
          </div>

        </div>

        {/* 4-Card Interactive Showcase Container */}
        <div className="relative z-20 -mb-6">
          <HeroClinicalShowcase 
            onSelectModule={onSelectModule}
            onExploreDemo={onExploreDemo}
          />
        </div>

      </section>

      {/* ========================================================================= */}
      {/* 2. TRANSITION SECTION - "CARE CAN BE COMPLEX. WE MAKE IT ☼ CLEAR."        */}
      {/* ========================================================================= */}
      <section className="py-24 px-6 sm:px-12 text-center max-w-6xl mx-auto">
        <h2 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-clarvos-black leading-tight mb-16">
          Specialized Care Can Be Complex.<br />
          We Make It <SunIcon size={44} className="mx-2" /> <span className="underline decoration-clarvos-yellow decoration-4 underline-offset-8">Clear.</span>
        </h2>

        {/* 4 Connected Pipeline Cards with dashed connection lines */}
        <div className="relative">
          {/* Subtle connecting line */}
          <div className="hidden lg:block absolute top-1/2 left-12 right-12 h-0.5 border-t-2 border-dashed border-clarvos-gray-300 -translate-y-6 z-0"></div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
            {[
              {
                step: '01',
                title: 'Assess',
                desc: 'Uncover sensory signals. Map clinical needs.',
                icon: <Brain className="w-4 h-4" />,
              },
              {
                step: '02',
                title: 'Personalize',
                desc: 'Craft multi-modal access that empowers.',
                icon: <Layers className="w-4 h-4" />,
              },
              {
                step: '03',
                title: 'Simulate',
                desc: 'Predict patient engagement and dwell comfort.',
                icon: <Activity className="w-4 h-4" />,
              },
              {
                step: '04',
                title: 'Empower',
                desc: 'Run smart automated clinical routines.',
                icon: <Zap className="w-4 h-4" />,
              },
            ].map((step, idx) => (
              <div
                key={idx}
                className="bg-white p-8 rounded-[32px] border border-clarvos-gray-300/80 shadow-sm hover:shadow-xl hover:border-clarvos-black transition-all duration-300 text-left flex flex-col justify-between"
              >
                <div>
                  <ClarvosPillBadge
                    number={step.step}
                    label={step.title}
                    icon={step.icon}
                    variant="dark"
                    className="mb-6"
                  />
                  <p className="text-clarvos-gray-600 text-sm leading-relaxed font-medium">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DISABILITY TAXONOMY SELECTOR (RPwD ACT 2016 & DSM-5) */}
      <section className="px-6 sm:px-12 max-w-7xl mx-auto mb-16">
        <div className="bg-[#f9f6f0] p-6 sm:p-8 rounded-[36px] border border-[#1c1b1f]/10 shadow-xs space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-black/10 pb-4">
            <div>
              <span className="text-[10px] font-black tracking-wider uppercase px-2.5 py-1 rounded-full bg-[#1c1b1f] text-[#ffd64e]">
                RPwD Act 2016 &amp; DSM-5 Taxonomy
              </span>
              <h3 className="text-xl font-black text-[#1c1b1f] mt-1.5 flex items-center gap-2">
                <Brain className="w-5 h-5 text-[#ffd64e]" />
                Explore 8 Prescribed Disability Wings
              </h3>
            </div>
            <button
              onClick={() => onExploreDemo('disabilities')}
              className="text-xs font-black text-[#1c1b1f] hover:text-amber-600 flex items-center gap-1 cursor-pointer"
            >
              <span>View All Statutory Mappings</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {DISABILITY_CLUSTERS.map((cluster) => {
              const CIcon = cluster.icon;
              return (
                <button
                  key={cluster.id}
                  onClick={() => onExploreDemo('disabilities')}
                  className="p-3 rounded-2xl bg-white hover:bg-[#ffd64e]/20 border border-black/10 flex items-center gap-2.5 text-left transition-all cursor-pointer shadow-2xs hover:border-[#1c1b1f]"
                >
                  <div className="w-8 h-8 rounded-xl bg-[#1c1b1f] text-[#ffd64e] flex items-center justify-center shrink-0">
                    <CIcon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-black text-[#1c1b1f] truncate">{cluster.shortName}</div>
                    <div className="text-[10px] text-slate-500 truncate">{cluster.rpwdAlignment}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 3. ALTERNATING HIGH-CONTRAST BENTO SECTIONS (01 - 04)                    */}
      {/* ========================================================================= */}

      {/* BENTO CARD 01: ASSESS (Soft Sand Card) */}
      <section className="px-6 sm:px-12 max-w-7xl mx-auto mb-16">
        <div className="bg-clarvos-sand p-8 sm:p-14 lg:p-16 rounded-[40px] border border-clarvos-gray-300 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          <div className="lg:col-span-5">
            <ClarvosPillBadge number="01" label="Assess" variant="dark" className="mb-6" />
            <h3 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight mb-4">
              Understand real patient intent without communication barriers.
            </h3>
            <p className="text-clarvos-gray-600 text-base leading-relaxed mb-8">
              AbilityOS gathers and processes bio-sensory feedback, gaze duration, and acoustic pitch in real time, so therapists can address the exact communication needs that matter most.
            </p>
            <ClarvosCTAButton
              text="Explore Disability Suites"
              hoverText="8 Clinical Wings"
              onClick={() => onExploreDemo('disabilities')}
              variant="dark"
              size="md"
            />
          </div>

          <div className="lg:col-span-7 bg-white p-6 sm:p-8 rounded-[32px] border border-clarvos-gray-300 shadow-lg">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-clarvos-gray-200">
              <span className="font-extrabold text-base">Active Clinical Signals</span>
              <span className="px-3 py-1 bg-clarvos-cream border border-clarvos-gray-300 text-xs font-mono font-bold rounded-full">
                Telemetry Engine
              </span>
            </div>

            <div className="space-y-3.5">
              {[
                { id: 1, title: 'Visual Dwell & Fixation Duration', score: '96%', cat: 'Motor & Gaze', spark: '▲ +14%' },
                { id: 2, title: 'AAC Icon Retrieval Speed', score: '92%', cat: 'Cognitive Access', spark: '▲ +22%' },
                { id: 3, title: 'Speech Formant Clarity Lift', score: '88%', cat: 'Speech Pathology', spark: '▲ +9%' },
                { id: 4, title: 'Sensory Overload Early Warning', score: '74%', cat: 'Neurodivergent Calm', spark: '▼ -38%' },
              ].map((row) => (
                <div key={row.id} className="p-3.5 rounded-2xl bg-clarvos-cream flex items-center justify-between gap-4 hover:bg-white hover:shadow-sm transition-all border border-transparent hover:border-clarvos-gray-300">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-clarvos-black text-white text-xs font-mono flex items-center justify-center font-bold">
                      {row.id}
                    </span>
                    <div>
                      <div className="font-bold text-sm text-clarvos-black">{row.title}</div>
                      <div className="text-[11px] text-clarvos-gray-600">{row.cat}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-md font-mono">{row.spark}</span>
                    <span className="font-mono font-extrabold text-sm text-clarvos-black bg-white px-2.5 py-1 rounded-lg border border-clarvos-gray-300">{row.score}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* BENTO CARD 02: PERSONALIZE (Solar Yellow Card) */}
      <section className="px-6 sm:px-12 max-w-7xl mx-auto mb-16">
        <div className="bg-clarvos-yellow p-8 sm:p-14 lg:p-16 rounded-[40px] shadow-xl grid grid-cols-1 lg:grid-cols-12 gap-12 items-center text-clarvos-black">
          
          <div className="lg:col-span-7 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              
              <div className="bg-white p-6 rounded-[28px] border border-black/10 shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-clarvos-mint text-emerald-950 flex items-center justify-center font-black">
                    AAC
                  </div>
                  <div>
                    <div className="font-bold text-sm">AAC Communication Board</div>
                    <div className="text-[11px] text-gray-500">Grid: 4x5, 6x6, High-Contrast</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-1.5 text-center text-[10px] font-bold">
                  <div className="p-2 bg-clarvos-cream rounded-lg border border-gray-200">Want</div>
                  <div className="p-2 bg-clarvos-cream rounded-lg border border-gray-200">Help</div>
                  <div className="p-2 bg-clarvos-yellow/40 rounded-lg border border-gray-300">Water</div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-[28px] border border-black/10 shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-clarvos-violet text-indigo-950 flex items-center justify-center font-black">
                    CP
                  </div>
                  <div>
                    <div className="font-bold text-sm">Cerebral Palsy Switch Access</div>
                    <div className="text-[11px] text-gray-500">Adaptive Dwell: 300ms - 800ms</div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs bg-clarvos-cream p-2.5 rounded-xl border border-gray-200 font-mono">
                  <span>Dwell Sensitivity</span>
                  <span className="font-bold text-clarvos-blue">Adaptive Auto</span>
                </div>
              </div>

            </div>

            {/* Brand Relevance Matrix */}
            <div className="bg-white p-6 rounded-[28px] border border-black/10 shadow-md">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Assistive Modality Matching</span>
                <span className="px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold font-mono">98% Match</span>
              </div>
              
              {/* Dot matrix visual bar like Clarvos */}
              <div className="flex items-center gap-1.5 my-3">
                {Array.from({ length: 24 }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-2.5 h-2.5 rounded-full ${i < 22 ? 'bg-emerald-500' : 'bg-gray-200'}`}
                  ></div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                {[
                  'Autism & Sensory', 
                  'Speech & Language', 
                  'Physical & Locomotor', 
                  'Neuro-Rehab & TBI', 
                  'Visual & Braille', 
                  'Hearing & ISL', 
                  'Dyslexia & Learning', 
                  'Vocational Transition'
                ].map((tag, i) => (
                  <button
                    key={i}
                    onClick={() => onExploreDemo('disabilities')}
                    className="px-3 py-1 rounded-full bg-clarvos-sand hover:bg-clarvos-yellow text-clarvos-black text-xs font-semibold transition-colors cursor-pointer border border-black/5 shadow-xs"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <ClarvosPillBadge number="02" label="Personalize" variant="dark" className="mb-6" />
            <h3 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight mb-4">
              Make an impression with tailored assistive interfaces.
            </h3>
            <p className="text-clarvos-charcoal text-base leading-relaxed mb-8">
              AbilityOS learns individual physical, cognitive, and sensory preferences: clinicians control how much to automate, with instant generation of personalized communication tiles, audio feedback, and high-contrast palettes.
            </p>
            <ClarvosCTAButton
              text="Explore Personalization Engine"
              hoverText="See Live Config"
              onClick={() => onExploreDemo('modules')}
              variant="dark"
              size="md"
            />
          </div>

        </div>
      </section>

      {/* BENTO CARD 03: SIMULATE (Interactive CP Dwell & R-PTA Metronome) */}
      <section className="px-6 sm:px-12 max-w-7xl mx-auto mb-16">
        <div className="bg-clarvos-sand p-8 sm:p-14 lg:p-16 rounded-[40px] border border-clarvos-gray-300 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          <div className="lg:col-span-5">
            <ClarvosPillBadge number="03" label="Simulate" variant="dark" className="mb-6" />
            <h3 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight mb-4">
              Reduce guesswork with predictive patient modeling.
            </h3>
            <p className="text-clarvos-gray-600 text-base leading-relaxed mb-8">
              The AbilityOS Simulator tests dwell thresholds and sensory stimuli before sessions take place. Experience the live interactive widgets right here.
            </p>
            <ClarvosCTAButton
              text="Open Full Simulator Hub"
              hoverText="Try All 8 Demos"
              onClick={() => onExploreDemo('modules')}
              variant="dark"
              size="md"
            />
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            
            {/* Interactive Dwell Simulator Widget */}
            <div className="bg-white p-7 rounded-[32px] border border-clarvos-gray-300 shadow-md flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-mono font-bold uppercase tracking-wider text-clarvos-gray-600">
                    Locomotor / CP Dwell
                  </span>
                  <span className="text-xs font-mono text-clarvos-black font-bold">{dwellDelay}ms</span>
                </div>

                <div className="text-center py-6">
                  <button
                    onMouseDown={handleDwellStart}
                    onMouseUp={handleDwellCancel}
                    onMouseLeave={handleDwellCancel}
                    onTouchStart={handleDwellStart}
                    onTouchEnd={handleDwellCancel}
                    className={`relative w-28 h-28 rounded-full font-extrabold text-sm flex items-center justify-center transition-all select-none mx-auto shadow-inner cursor-pointer ${
                      dwellSuccess 
                        ? 'bg-emerald-500 text-white shadow-lg scale-105' 
                        : 'bg-clarvos-cream text-clarvos-black border-2 border-clarvos-gray-300 hover:border-clarvos-yellow'
                    }`}
                  >
                    {/* Circular Dwell Progress Border */}
                    <div 
                      className="absolute inset-0 rounded-full border-4 border-clarvos-yellow transition-all"
                      style={{ clipPath: `polygon(0 0, 100% 0, 100% ${dwellProgress}%, 0 ${dwellProgress}%)` }}
                    />
                    <span className="relative z-10">
                      {dwellSuccess ? 'Activated!' : dwellProgress > 0 ? `${dwellProgress}%` : 'Hold / Dwell'}
                    </span>
                  </button>
                  <p className="text-[11px] text-clarvos-gray-600 mt-4">
                    Press & hold to simulate eye-gaze / switch hold
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-clarvos-gray-200">
                <div className="flex justify-between text-[11px] text-clarvos-gray-600 mb-1">
                  <span>Dwell Sensitivity:</span>
                  <span className="font-mono font-bold">{dwellDelay}ms</span>
                </div>
                <input
                  type="range"
                  min="200"
                  max="1000"
                  step="50"
                  value={dwellDelay}
                  onChange={(e) => setDwellDelay(parseInt(e.target.value))}
                  className="w-full accent-clarvos-black"
                />
              </div>
            </div>

            {/* Interactive Metronome & Braille Widget */}
            <div className="bg-white p-7 rounded-[32px] border border-clarvos-gray-300 shadow-md flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-mono font-bold uppercase tracking-wider text-clarvos-gray-600">
                    Neuro-Rhythm Metronome
                  </span>
                  <span className="text-xs font-mono text-clarvos-black font-bold">{rasBpm} BPM</span>
                </div>

                <div className="text-center py-4">
                  <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center transition-all ${
                    isMetronomeActive ? 'bg-clarvos-yellow shadow-lg scale-110' : 'bg-clarvos-cream border border-clarvos-gray-300'
                  }`}>
                    <Music className={`w-8 h-8 ${isMetronomeActive ? 'text-clarvos-black animate-pulse' : 'text-gray-400'}`} />
                  </div>

                  <button
                    onClick={() => setIsMetronomeActive(!isMetronomeActive)}
                    className={`mt-4 px-5 py-2 rounded-full font-bold text-xs transition-all shadow cursor-pointer ${
                      isMetronomeActive ? 'bg-rose-600 text-white' : 'bg-clarvos-black text-white hover:bg-clarvos-charcoal'
                    }`}
                  >
                    {isMetronomeActive ? 'Stop Rhythm' : 'Start Audio Metronome'}
                  </button>
                </div>
              </div>

              <div className="pt-4 border-t border-clarvos-gray-200">
                <div className="text-[11px] text-clarvos-gray-600 mb-1 font-bold">Braille Grade 1 Live:</div>
                <div className="font-mono text-base tracking-widest text-clarvos-black bg-clarvos-cream p-2 rounded-xl border border-clarvos-gray-300">
                  {toBraille(brailleInput)}
                </div>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* BENTO CARD 04: EMPOWER (Solar Yellow Card) */}
      <section className="px-6 sm:px-12 max-w-7xl mx-auto mb-20">
        <div className="bg-clarvos-yellow p-8 sm:p-14 lg:p-16 rounded-[40px] shadow-xl grid grid-cols-1 lg:grid-cols-12 gap-12 items-center text-clarvos-black">
          
          <div className="lg:col-span-7 bg-white p-6 sm:p-8 rounded-[32px] border border-black/10 shadow-lg space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-gray-200">
              <div>
                <h4 className="font-extrabold text-lg">Multi-Device Clinical Synchronization</h4>
                <p className="text-xs text-gray-500">Live sync across schools, homes, and therapy centers</p>
              </div>
              <span className="px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-bold rounded-full font-mono">
                Real-time WebSocket
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-clarvos-cream p-4 rounded-2xl border border-gray-200 text-center">
                <Smartphone className="w-8 h-8 mx-auto mb-2 text-clarvos-black" />
                <div className="font-bold text-xs mb-1">Android & iOS PWA</div>
                <div className="text-[10px] text-gray-500">Instant offline caching</div>
              </div>
              <div className="bg-clarvos-cream p-4 rounded-2xl border border-gray-200 text-center">
                <Cpu className="w-8 h-8 mx-auto mb-2 text-clarvos-black" />
                <div className="font-bold text-xs mb-1">Wheelchair Displays</div>
                <div className="text-[10px] text-gray-500">HID switch compatible</div>
              </div>
              <div className="bg-clarvos-cream p-4 rounded-2xl border border-gray-200 text-center">
                <Globe className="w-8 h-8 mx-auto mb-2 text-clarvos-black" />
                <div className="font-bold text-xs mb-1">Clinician Portal</div>
                <div className="text-[10px] text-gray-500">One-click audit trail</div>
              </div>
            </div>

            <div className="bg-clarvos-sand/60 p-4 rounded-2xl flex items-center justify-between text-xs">
              <span className="font-medium text-clarvos-charcoal">EVV GPS Clinic Check-in & Session Notes:</span>
              <span className="font-bold font-mono text-emerald-800">100% Automated</span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <ClarvosPillBadge number="04" label="Empower" variant="dark" className="mb-6" />
            <h3 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight mb-4">
              Deploy unified workflows across schools, clinics, and tablets.
            </h3>
            <p className="text-clarvos-charcoal text-base leading-relaxed mb-8">
              AbilityOS Agentic Workflow builds and runs your optimal care setup. Engage across diverse assistive channels without manually synchronizing profiles or rewriting therapy goals.
            </p>
            <ClarvosCTAButton
              text="Download Native Apps"
              hoverText="Get APK & PWA"
              onClick={() => onExploreDemo('downloads')}
              variant="dark"
              size="md"
            />
          </div>

        </div>
      </section>

      {/* ========================================================================= */}
      {/* 3.5. CLINICAL COMPETITOR & TCO COMPARISON MATRIX                          */}
      {/* ========================================================================= */}
      <ClinicalComparisonChart 
        onExploreDemo={onExploreDemo}
        onSelectModule={onSelectModule}
      />

      {/* ========================================================================= */}
      {/* 4. OVERSIZED FLOATING METRIC PILLS                                        */}
      {/* ========================================================================= */}
      <section className="py-16 px-6 sm:px-12 max-w-6xl mx-auto text-center">
        <p className="text-sm font-bold uppercase tracking-widest text-clarvos-gray-600 mb-8">
          Accelerate Clinical Outcomes. Minimize Administrative Overhead.
        </p>

        <div className="space-y-6">
          {/* Metric Pill 1 */}
          <div className="inline-flex items-center gap-4 sm:gap-6 bg-white px-8 sm:px-14 py-5 sm:py-7 rounded-full border border-clarvos-gray-300 shadow-lg max-w-full text-2xl sm:text-5xl font-extrabold tracking-tight hover:scale-[1.02] transition-transform duration-500">
            <span>In minutes</span>
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-clarvos-yellow text-clarvos-black flex items-center justify-center">
              <Clock className="w-6 h-6 sm:w-8 sm:h-8" />
            </div>
            <span>not sessions</span>
          </div>

          <br />

          {/* Metric Pill 2 */}
          <div className="inline-flex items-center gap-4 sm:gap-6 bg-white px-8 sm:px-14 py-5 sm:py-7 rounded-full border border-clarvos-gray-300 shadow-lg max-w-full text-2xl sm:text-5xl font-extrabold tracking-tight hover:scale-[1.02] transition-transform duration-500">
            <span>Up to</span>
            <div className="px-5 sm:px-8 py-2 sm:py-3 rounded-full bg-clarvos-yellow text-clarvos-black font-black text-2xl sm:text-4xl">
              85%
            </div>
            <span>clinical time saved</span>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 5. BIG BUBBLE ARROW CTA SECTION                                           */}
      {/* ========================================================================= */}
      <section className="bg-clarvos-black text-white py-28 px-6 sm:px-12 rounded-[40px] mt-16 max-w-7xl mx-auto text-center relative overflow-hidden">
        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="text-4xl sm:text-6xl font-extrabold tracking-tight mb-4">
            See it in action
          </h2>
          <p className="text-lg sm:text-xl text-clarvos-gray-300 mb-12">
            Let us show you how AbilityOS accelerates patient independence and unifies care teams.
          </p>

          {/* Huge Expandable Button CTA like Clarvos */}
          <div className="flex justify-center">
            <div
              onClick={handleDemoClick}
              className="btn-bubble-arrow bg-white text-clarvos-black px-10 py-6 sm:py-8 sm:px-16 cursor-pointer shadow-2xl group flex items-center gap-6"
            >
              <div className="cta-button_bg"></div>
              
              <div className="cta-button_arrow w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-clarvos-black text-white flex items-center justify-center transition-transform">
                <ArrowUpRight className="w-7 h-7 sm:w-9 sm:h-9" />
              </div>

              <div className="button-slide-text text-2xl sm:text-4xl font-extrabold relative z-10">
                <div className="button-slide-inner group-hover:text-white transition-colors duration-500">
                  <span>Request a Demo</span>
                  <span className="text-clarvos-yellow">Ready to Join?</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 6. FAQ ACCORDION SECTION                                                  */}
      {/* ========================================================================= */}
      <section className="py-24 px-6 sm:px-12 max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <ClarvosPillBadge label="Answers" variant="dark" className="mb-4" />
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight">
            Frequently Asked Questions
          </h2>
        </div>

        <div className="space-y-4">
          {[
            {
              q: 'How does AbilityOS differ from traditional standalone AAC software?',
              a: 'Traditional AAC software only offers isolated speech boards. AbilityOS is a unified clinical operating system that unifies AAC, biomechanical CP motor dwell calibration, dyslexia typography, neuro-rhythmic metronomes, and automated IEP clinical documentation under one synchronized platform.',
            },
            {
              q: 'Can our school district or clinic run AbilityOS offline?',
              a: 'Yes. AbilityOS includes offline Progressive Web App (PWA) and native Android APK capabilities with Dexie.js local database caching. Sessions continue smoothly without active internet and sync back to cloud servers automatically when connection is restored.',
            },
            {
              q: 'Is student health data protected and HIPAA / FERPA compliant?',
              a: 'Completely. All telemetry records and student therapy logs are encrypted both in transit and at rest with zero-knowledge keys, fully compliant with HIPAA, FERPA, and SOC2 standards.',
            },
            {
              q: 'What languages and voice models are supported out-of-the-box?',
              a: 'AbilityOS supports high-definition neural voice synthesis across English, Hindi, Marathi, and Spanish with customizable pitch, speed, and gender modulation, alongside full Braille Grade 1 and 2 translation.',
            },
          ].map((faq, i) => (
            <div
              key={i}
              className="bg-white rounded-[24px] border border-clarvos-gray-300 overflow-hidden transition-all duration-300"
            >
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full px-7 py-6 text-left font-bold text-lg flex items-center justify-between gap-4 cursor-pointer"
              >
                <span>{faq.q}</span>
                <div className={`w-8 h-8 rounded-full bg-clarvos-cream flex items-center justify-center transition-transform duration-300 ${
                  openFaq === i ? 'rotate-180 bg-clarvos-yellow' : ''
                }`}>
                  <ChevronDown className="w-4 h-4 text-clarvos-black" />
                </div>
              </button>
              {openFaq === i && (
                <div className="px-7 pb-6 text-clarvos-gray-600 text-sm sm:text-base leading-relaxed border-t border-clarvos-gray-200/60 pt-4">
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

    </div>
  );
};
